import type { QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { MessageInstance } from "antd/es/message/interface";
import { closeBill, closeBillRetry, isUnknownFiscalOutcome } from "../../../../../api/bills";
import type {
  BillOut,
  CloseBillFiscalInvoiceIn,
  CloseBillResult,
  CloseBillTipIn,
} from "../../../../../api/bills";
import { fiscalInvoiceOutcomeOf, hasFiscalDocument } from "../../../../../lib/fiscalInvoice";
import {
  forgetRefusedFiscalInvoiceBuyer,
  rememberRefusedFiscalInvoiceBuyer,
} from "../invoicing/refusedFiscalInvoiceBuyers";
import {
  billClosingFinished,
  billClosingStarted,
  type AppDispatch,
} from "../../../../../store";
import { getApiErrorMessage } from "../../../../../utils/translations";

/** Изборът от модала „Закрий" — тялото е едно и също за `/close` и `/close/retry`. */
export interface CloseBillSelection {
  billId: number;
  paymentMethodId: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  total?: number;
  /** Бакшишът — само при бакшиш > 0; без него тялото е старото. */
  tip?: CloseBillTipIn;
  /** Плащане със закачения депозит (без бакшиш, не при retry). */
  depositId?: number;
  /** Повторен опит на сметка, платена с депозит: тялото на `/close/retry` е
   *  ПРАЗНО — BE повтаря бона със записаните метод и депозит (както
   *  „Фискални проблеми"); `paymentMethodId` тогава не се праща. */
  retryWithRecordedPayment?: boolean;
  /** Фискална фактура при закриване — купувачът; само към `/close` (повторният
   *  опит я взима от записания payload на бона). */
  fiscalInvoice?: CloseBillFiscalInvoiceIn;
}

interface BackgroundBillCloseOptions {
  selection: CloseBillSelection;
  /** Закрита без бон сметка — същото тяло, но към `/close/retry`. */
  isRetry: boolean;
  /** Повторен опит, чийто ЗАПИСАН бакшиш е с карта: тялото не го носи, но
   *  терминалът пак може да чака човек — картовият бюджет на чакането. */
  retryWaitsForCardTip?: boolean;
  tableName: string;
  snapshot: BillOut | null;
  userId: number | null;
  dispatch: AppDispatch;
  queryClient: QueryClient;
  message: MessageInstance;
  notifyPrinterError: (text: string) => void;
  notifyPrinterTimeout: (text: string) => void;
  t: TFunction;
}

/**
 * Закриването СЛЕД като модалът „Закрий" е затворен: заявката е същата като
 * преди и чака принтера със същите таймаути — само че докато трае, се върти
 * кутийката на масата, а сервитьорът е свободен (docs/ordering.md §Поток:
 * закриване, плащане, фискализация).
 *
 * Не е hook: живее извън компонента, за да довърши и когато сервитьорът е
 * минал в продуктовия изглед (футърът с модала се демонтира) или в друг
 * модул. Затова всичко, което ползва, идва отвън — message/notification
 * инстанциите са на `App` в корена и остават живи.
 *
 * Изходът е като преди, но известието носи масата: успех → toast; неясен
 * изход (`timeout`/`unknown` — бонът може да е на хартия) → кехлибарено, отказ
 * → червено, и двете постоянни до ръчно затваряне; 409 → сметката вече е
 * закрита от друг. После кутийката приема каквото върне сървърът — изчезва,
 * или остава „закрита, нефискализирана" според настройката на ресторанта.
 */
export async function runBackgroundBillClose({
  selection,
  isRetry,
  retryWaitsForCardTip = false,
  tableName,
  snapshot,
  userId,
  dispatch,
  queryClient,
  message,
  notifyPrinterError,
  notifyPrinterTimeout,
  t,
}: BackgroundBillCloseOptions): Promise<void> {
  const {
    billId,
    tip,
    depositId,
    retryWithRecordedPayment,
    fiscalInvoice,
    ...paymentSelection
  } = selection;
  const retryBody = retryWithRecordedPayment ? {} : paymentSelection;
  // Повторното издаване на бон е за сметка, която отдавна е ПЛАТЕНА — отказът
  // там не е „неуспешно плащане" (сервитьорът би взел парите втори път).
  const failureKey = isRetry
    ? "closeTable.tableNotice"
    : "closeTable.paymentFailed";
  dispatch(billClosingStarted({ billId, snapshot, userId }));
  let result: CloseBillResult | undefined;
  try {
    // Бакшишът е последен в тялото и го има само при бакшиш > 0 — иначе
    // извикването е буквално старото. Повторният опит („Закрий" на закрита
    // без бон) НИКОГА не го праща: BE ползва записания при закриването
    // (cross-repo-contract ред 46).
    result = isRetry
      ? retryWaitsForCardTip
        ? await closeBillRetry(billId, retryBody, { waitsForCardTerminal: true })
        : await closeBillRetry(billId, retryBody)
      : fiscalInvoice
        ? // Фискалната фактура е последна в тялото — с депозита или бакшиша на
          // сметката, ако ги има (без нея извикванията долу са старите).
          await closeBill(
            billId,
            paymentSelection.paymentMethodId,
            paymentSelection.mixedCashAmount,
            paymentSelection.mixedCardAmount,
            paymentSelection.total,
            tip,
            depositId,
            fiscalInvoice,
          )
      : depositId !== undefined
        ? // „Смесено" с депозит — сумите са на остатъка след него.
          await closeBill(
            billId,
            paymentSelection.paymentMethodId,
            paymentSelection.mixedCashAmount,
            paymentSelection.mixedCardAmount,
            undefined,
            undefined,
            depositId,
          )
        : tip
        ? await closeBill(
            billId,
            paymentSelection.paymentMethodId,
            paymentSelection.mixedCashAmount,
            paymentSelection.mixedCardAmount,
            paymentSelection.total,
            tip,
          )
        : await closeBill(
            billId,
            paymentSelection.paymentMethodId,
            paymentSelection.mixedCashAmount,
            paymentSelection.mixedCardAmount,
            paymentSelection.total,
          );
    // Закриването е минало — запомненият от предишен отказ купувач е излишен.
    if (fiscalInvoice) forgetRefusedFiscalInvoiceBuyer(billId);
    // Фискалната фактура казва своето (номерата или незаписания документ) —
    // и при повторното издаване: `/close/retry` я печата от записания payload
    // на бона, без поле в тялото. `finally` долу пак опреснява списъците.
    if (notifyFiscalInvoiceOutcome(result, tableName, message, notifyPrinterTimeout, t)) {
      return;
    }
    const fiscalResult = result?.fiscal_result;
    if (!fiscalResult || fiscalResult.status === "success") {
      message.success(t("closeTable.closeSuccess"));
    } else if (isUnknownFiscalOutcome(fiscalResult.status)) {
      notifyPrinterTimeout(
        t("closeTable.tableNotice", {
          table: tableName,
          text: fiscalResult.error || t("closeTable.printerTimeout"),
        }),
      );
    } else {
      notifyPrinterError(
        t(failureKey, {
          table: tableName,
          text: fiscalResult.error || t("closeTable.closeError"),
        }),
      );
    }
  } catch (error) {
    const response = (
      error as { response?: { status?: number; data?: { detail?: unknown } } }
    )?.response;
    const status = response?.status;
    // Фискалната фактура се отказва ПРЕДИ закриването (при повторното
    // издаване — преди бона) и с 409 `{bg,en}` (сметката вече има фактура,
    // поредицата се е разминала…): текстът е причината, не „вече е закрита".
    // „Вече е закрита" идва с текст, не с обект.
    const fiscalInvoiceRefusal =
      status === 409 &&
      typeof response?.data?.detail === "object" &&
      response.data.detail !== null;
    // 409 = сметката вече е затворена от паралелна заявка (два таба, две
    // устройства, мрежов retry). Закриването Е минало — оранжево
    // предупреждение, а не червена грешка; бонът — виж „Фискални проблеми".
    if (status === 409 && !fiscalInvoiceRefusal) {
      message.warning(
        t("closeTable.tableNotice", {
          table: tableName,
          text: t("closeTable.alreadyClosed"),
        }),
      );
    } else {
      // Сметката е още отворена: следващият избор ѝ във „Фактуриране“ предлага
      // купувача попълнен, вместо да се въвежда наново.
      if (fiscalInvoice) rememberRefusedFiscalInvoiceBuyer(billId, fiscalInvoice);
      notifyPrinterError(
        t(failureKey, {
          table: tableName,
          text: getApiErrorMessage(error, t("closeTable.closeError")),
        }),
      );
      console.error("Error closing bill:", error);
    }
  } finally {
    // Първо свежият списък, чак после кутийката спира да се върти — иначе за
    // миг би се показала старата (още отворена) сметка от кеша.
    await queryClient
      .invalidateQueries({ queryKey: ["bills"] })
      .catch(() => undefined);
    // Използваният депозит вече не е активен; при повтор на сметка с депозит
    // списъците на депозитите също се опресняват (сметката в справката).
    if (depositId !== undefined || retryWithRecordedPayment) {
      void queryClient.invalidateQueries({ queryKey: ["deposits"] });
    }
    // Фискалната фактура е нов запис във „Фактури" (и следващият номер на
    // поредицата), а купувачът — в регистъра на клиентите. Повторното
    // издаване я носи само в отговора.
    if (fiscalInvoice || hasFiscalDocument(result)) {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["bills", "uninvoiced"] });
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    }
    dispatch(billClosingFinished(billId));
  }
}

/**
 * Изходът на закриване (или повторно издаване) с фискална фактура, когато
 * бонът Е излязъл: записаната фактура → toast „Фактура № … · бон № …"; бонът
 * е на хартия, а записът в облака не е станал (`fiscal_invoice_error`) →
 * кехлибарено постоянно известие — документът не е изгубен, а стои във
 * „Фискални проблеми". `true` — казано е; `false` — изходът е обичайният
 * (отказ, неизвестен бон, бон без фактура).
 */
function notifyFiscalInvoiceOutcome(
  result: CloseBillResult | undefined,
  tableName: string,
  message: MessageInstance,
  notifyPrinterTimeout: (text: string) => void,
  t: TFunction,
): boolean {
  const outcome = fiscalInvoiceOutcomeOf(result, tableName, t);
  if (outcome?.kind === "recordFailed") {
    notifyPrinterTimeout(t("closeTable.tableNotice", { table: tableName, text: outcome.text }));
  } else if (outcome) {
    message.success(outcome.text);
  }
  return outcome !== null;
}
